import {
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { authConfig, msalConfiguration } from '@/config/authConfig';

function resolveLogoutHint(account: AccountInfo | null): string | undefined {
  if (!account) {
    return undefined;
  }

  const claims = account.idTokenClaims as
    | {
        login_hint?: string;
        preferred_username?: string;
        upn?: string;
      }
    | undefined;

  return (
    claims?.login_hint ??
    claims?.preferred_username ??
    claims?.upn ??
    account.username ??
    undefined
  );
}

class AuthService {
  private readonly msal = new PublicClientApplication(msalConfiguration);

  private isInitialized = false;

  /**
   * Get the underlying MSAL instance.
   * Used by MsalPopupAuthService to share the same instance for token cache coherency.
   */
  getMsalInstance(): PublicClientApplication {
    return this.msal;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    await this.msal.initialize();
    this.isInitialized = true;
  }

  async handleRedirectResponse(): Promise<AuthenticationResult | null> {
    const response = await this.msal.handleRedirectPromise();

    if (response?.account) {
      this.msal.setActiveAccount(response.account);
    }

    return response;
  }

  getActiveAccount(): AccountInfo | null {
    return this.msal.getActiveAccount();
  }

  getAllAccounts(): AccountInfo[] {
    return this.msal.getAllAccounts();
  }

  setActiveAccount(account: AccountInfo | null): void {
    this.msal.setActiveAccount(account);
  }

  getAccountById(accountId: string): AccountInfo | null {
    const account = this.msal
      .getAllAccounts()
      .find(
        (item) =>
          item.homeAccountId === accountId || item.localAccountId === accountId,
      );

    return account ?? null;
  }

  async login(redirectStartPage?: string): Promise<void> {
    await this.msal.loginRedirect({
      scopes: authConfig.scopes,
      redirectStartPage,
    });
  }

  async logout(): Promise<void> {
    const account =
      this.msal.getActiveAccount() ?? this.msal.getAllAccounts()[0] ?? null;

    await this.msal.logoutRedirect({
      account: account ?? undefined,
      logoutHint: resolveLogoutHint(account),
      postLogoutRedirectUri: authConfig.postLogoutRedirectUri,
    });
  }

  async acquireTokenSilently(account: AccountInfo): Promise<void> {
    await this.acquireToken(account, authConfig.scopes);
  }

  async acquireToken(
    account: AccountInfo,
    scopes: string[],
  ): Promise<AuthenticationResult> {
    return this.msal.acquireTokenSilent({
      account,
      scopes,
    });
  }

  async acquireAccessToken(
    scopes: string[] = authConfig.apiScopes,
  ): Promise<string | null> {
    try {
      await this.initialize();

      let account = this.msal.getActiveAccount();

      if (!account) {
        const accounts = this.msal.getAllAccounts();
        account = accounts[0] ?? null;

        if (account) {
          this.msal.setActiveAccount(account);
        }
      }

      if (!account) {
        return null;
      }

      const tokenResult = await this.acquireToken(account, scopes);

      return tokenResult.accessToken ?? null;
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
